/**
 * P8 — page meta module. buildMeta(type, data) -> { title, description,
 * canonical, robots, og }. Descriptions are clamped to <=155 chars, carry no
 * counts, and avoid banned words. Titles follow the approved templates.
 */
const SITE = 'ArtistScanner';
const CITY = 'Delhi NCR';
// Share previews need absolute URLs on the public domain (WhatsApp / Facebook ignore relative ones).
export const SITE_ORIGIN = 'https://artistscanner.ai';
export const OG_DEFAULT_IMAGE = `${SITE_ORIGIN}/brand/og-default.png`;

// Artist cover photo as a 1200x630 share image (Cloudinary crop); brand default when none.
const artistOgImage = (url) => {
  const u = String(url || '');
  if (u.includes('res.cloudinary.com') && u.includes('/image/upload/')) {
    return u.replace('/image/upload/', '/image/upload/c_fill,g_auto,w_1200,h_630,f_auto,q_auto/');
  }
  return /^https?:\/\//.test(u) ? u : OG_DEFAULT_IMAGE;
};

const clip = (s, n = 155) => {
  if (!s) return s;
  const t = s.trim();
  return t.length > n ? t.slice(0, n - 1).trimEnd() + '…' : t;
};

const singular = (cat) => {
  if (!cat) return 'Performer';
  return cat.replace(/s$/i, '');
};

export function buildMeta(type, data = {}) {
  const year = new Date().getFullYear();
  const name = data.name || '';
  const category = data.category || 'Live Performers';
  const city = data.city || CITY;
  const area = data.area || CITY;
  const zone = data.zone || CITY;
  const occasion = data.occasion || 'celebration';
  const top3 =
    data.topCategories && data.topCategories.length
      ? data.topCategories.slice(0, 3).join(', ')
      : 'Singers, DJs & Bands';

  let title;
  let description;
  let robots = 'index,follow';
  let og = {};

  switch (type) {
    case 'home':
      title = `Book Live Performers in ${CITY} for Home & Office Events | ${SITE}`;
      description =
        'Find singers, DJs, anchors, dhol & tabla players and comedians near you. Prices listed. Call artists directly. We take nothing.';
      break;
    case 'listing':
    case 'category':
      title = `${category} in ${CITY} for Birthdays, House Parties & Functions`;
      description = `Browse ${category.toLowerCase()} in ${CITY} with prices listed. Watch videos and call the artist directly — no sign-up, no commission.`;
      break;
    case 'occasion':
      title = `${occasion} Entertainment in ${CITY} – ${top3}`;
      description = `Who to call for ${/^[aeiou]/i.test(occasion) ? 'an' : 'a'} ${occasion} in ${CITY}. ${top3} with prices listed. Contact artists directly.`;
      break;
    case 'occasionCategory':
      title = `${singular(category)} for ${occasion} in ${CITY} – Book Live ${category}`;
      description = `Find a ${singular(category).toLowerCase()} for your ${occasion} in ${CITY}. Prices listed by the artist. Call directly.`;
      break;
    case 'categoryArea':
      title = `${category} in ${area}, ${zone} – Live ${category} for Home Parties`;
      description = `${category} in ${area}, ${zone} for home parties and functions. Prices listed. Contact the artist directly.`;
      break;
    case 'artist':
      title = `${name} – ${category} in ${city} | Videos, Price & Contact`;
      description = `${name} – ${category} in ${city}. Watch videos, see the listed price and contact directly on WhatsApp.`;
      og = {
        title: `${name} – ${category} in ${city} | ${SITE}`,
        description: [data.priceRange, data.area || city, 'videos, price & WhatsApp contact'].filter(Boolean).join(' · '),
        image: artistOgImage(data.image),
        type: 'profile',
        url: data.ogUrl,
      };
      break;
    case 'artistReviews':
      title = `${name} Reviews – ${category} in ${city}`;
      description = `Read reviews for ${name}, ${category} in ${city}, from hosts who found them on ${SITE}.`;
      robots = 'noindex,follow';
      og = { image: artistOgImage(data.image), type: 'profile', url: data.ogUrl };
      break;
    case 'collection':
      title = `${name} – ${CITY} | ${SITE}`;
      description = `${name} in ${CITY}. Watch videos, see listed prices and contact artists directly.`;
      break;
    case 'priceGuide':
      title = `${singular(category)} Price in ${CITY} for Birthday & House Parties (${year})`;
      description = `What a ${singular(category).toLowerCase()} costs in ${CITY} for birthdays and house parties, with prices listed by the artists.`;
      break;
    case 'artistLanding':
      title = `List as a Performer in ${CITY} – Free, No Commission | ${SITE}`;
      description =
        'Get found by hosts near you. List free, keep every rupee — hosts contact you directly. No commission, ever.';
      break;
    case 'artistSignup':
      title = `List as an artist – ${SITE}`;
      description =
        'Get found by hosts near you. List free, keep every rupee — hosts contact you directly. No commission, ever.';
      break;
    case 'post':
      title = `Tell us what you need – ${SITE}`;
      description =
        'Post your occasion and we send it to up to five matched artists near you. They contact you directly.';
      robots = 'noindex,follow';
      break;
    case 'search':
      title = `Search – ${SITE}`;
      description = `Search for artists near you on ${SITE}.`;
      robots = 'noindex,follow';
      break;
    case 'saved':
      title = `Saved artists – ${SITE}`;
      description = `Artists you saved on ${SITE}.`;
      robots = 'noindex,follow';
      break;
    case 'review':
      title = `${data.title || 'Review'} – ${SITE}`;
      description = `Share your experience on ${SITE}.`;
      robots = 'noindex,follow';
      break;
    case 'dashboard':
      title = `${data.title || 'Dashboard'} – ${SITE}`;
      description = `Your ${SITE} dashboard.`;
      robots = 'noindex,follow';
      break;
    case 'legal':
      title = `${data.title} – ${SITE}`;
      description = data.description || `${data.title} – ${SITE}.`;
      break;
    case 'auth':
      title = `${data.title || 'Sign in'} – ${SITE}`;
      description = `Sign in to ${SITE}.`;
      robots = 'noindex,follow';
      break;
    default:
      title = SITE;
      description = `Find performers near you in ${CITY}. Prices listed. Call directly.`;
  }

  return {
    title,
    description: clip(description, 155),
    robots,
    canonical: data.canonical || null,
    og: {
      title: og.title || title,
      description: clip(og.description || description, 200),
      image: og.image || OG_DEFAULT_IMAGE,
      type: og.type || 'website',
      url: og.url || data.canonical || null,
    },
  };
}

export default buildMeta;
